import { Card, ExternalLink, Icon, Section, Tag } from '@ui/library';
import type { Experience as ExperienceData } from '@lib/schemas/experience';

export interface ExperienceCopy {
  eyebrow: string;
  title: string;
  linkedinLinkLabel: string;
  skillsEyebrow: string;
  skillsTitle: string;
  educationSubhead: string;
  recognitionSubhead: string;
  arxiv: {
    id: string;
    fullTitle: string;
  };
}

export interface ExperienceProps {
  experience: ExperienceData;
  linkedinHref: string;
  arxivHref: string;
  copy: ExperienceCopy;
}

export function Experience({ experience, linkedinHref, arxivHref, copy }: ExperienceProps) {
  const { roles, skills, education, recognition } = experience;

  return (
    <>
      <Section
        id="experience"
        eyebrow={copy.eyebrow}
        title={copy.title}
        link={{ label: copy.linkedinLinkLabel, href: linkedinHref }}
      >
        <div className="xp reveal" style={{ marginTop: 36 }}>
          {roles.map((x) => (
            <div className="xp__row" key={`${x.where}-${x.when}`}>
              <div className="xp__when">
                <b>{x.where}</b>
                {x.when}
              </div>
              <div className="xp__role">
                <h4>{x.role} <em>· {x.company}</em></h4>
                <p>{x.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="skills" tight eyebrow={copy.skillsEyebrow} title={copy.skillsTitle}>
        <div className="skills" style={{ marginTop: 40 }}>
          {skills.map(([group, items]) => (
            <Card key={group} variant="skillcard">
              <h4>{group}</h4>
              <div className="skillcard__tags">
                {items.map((s) => <Tag key={s}>{s}</Tag>)}
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section id="education" tight>
        <div className="edugrid">
          <div className="edu reveal">
            <div className="subhead">{copy.educationSubhead}</div>
            {education.map((e) => (
              <div className="edu__row" key={e.deg}>
                <div className="edu__when">{e.years}</div>
                <div>
                  <h4>{e.deg}</h4>
                  <span className="edu__school">{e.school}</span>
                  {e.note && <p className="edu__note">{e.note}</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="recog reveal">
            <div className="subhead">{copy.recognitionSubhead}</div>
            <ul className="recog__list">
              {recognition.map((r) => (
                <li key={r}><Icon name="sparkles" size={15} />{r}</li>
              ))}
            </ul>
            <ExternalLink href={arxivHref} className="recog__paper">
              <Icon name="file-text" size={18} />
              <span>
                <b>{copy.arxiv.id}</b>
                <span className="recog__sub">{copy.arxiv.fullTitle}</span>
              </span>
              <Icon name="arrow-up-right" size={16} />
            </ExternalLink>
          </div>
        </div>
      </Section>
    </>
  );
}
